/**
 * L.4 — NIPE tool: recommendCorpus
 *
 * Read-only. Given a free-text goal, return 3-5 Skill names that
 * would make a good specialization corpus for that goal. Reads from
 * the same `listSkills` context other tools use; uses a simple
 * keyword-overlap heuristic (no LLM round-trip) so the recommendation
 * is fast and deterministic.
 *
 * The actual specialization kickoff happens elsewhere (TrainLoraButton
 * single-skill, future multi-skill bundle entry — L.6). This tool is
 * purely a discovery aid for the chat.
 */

import type { NipeTool } from './types';

export interface RecommendCorpusContext {
  listSkills: () => Promise<
    Array<{ name: string; description?: string; body?: string }>
  >;
}

interface ScoredSkill {
  name: string;
  description: string;
  score: number;
}

/**
 * Lightweight tokenizer — lower-case, split on non-letter runs, drop
 * tokens of length < 3 to skip stop-words.
 */
function tokenize(text: string): Set<string> {
  const out = new Set<string>();
  for (const tok of text.toLowerCase().split(/[^a-z0-9]+/)) {
    if (tok.length >= 3) out.add(tok);
  }
  return out;
}

export const recommendCorpusTool: NipeTool<RecommendCorpusContext> = {
  schema: {
    type: 'function',
    function: {
      name: 'recommendCorpus',
      description:
        "Recommend 3-5 Skills to specialize the model on, given a user's free-text goal (e.g. 'I want to specialize for litigation drafting'). Returns Skill names ranked by overlap with the goal.",
      parameters: {
        type: 'object',
        properties: {
          goal: {
            type: 'string',
            description:
              "User's goal in their own words (e.g. 'help me train an apprentice for estate planning' or 'corporate M&A workflow').",
          },
          limit: {
            type: 'number',
            description:
              'Optional maximum number of Skills to return. Defaults to 5; clamped to [1, 10].',
          },
        },
        required: ['goal'],
      },
    },
  },
  mutatesRemote: false,
  requiresApproval: false,
  handler: async (args, ctx) => {
    const goal = typeof args.goal === 'string' ? args.goal.trim() : '';
    if (!goal) return { error: 'goal required' };
    const requestedLimit = typeof args.limit === 'number' ? args.limit : 5;
    const limit = Math.max(1, Math.min(10, Math.floor(requestedLimit)));

    const goalTokens = tokenize(goal);
    if (goalTokens.size === 0) {
      return {
        goal,
        skills: [],
        note: 'Goal had no usable tokens; nothing to recommend.',
      };
    }

    const skills = await ctx.listSkills();
    const scored: ScoredSkill[] = skills.map((s) => {
      const haystack = `${s.name} ${s.description ?? ''}`;
      const tokens = tokenize(haystack);
      let overlap = 0;
      for (const t of goalTokens) {
        if (tokens.has(t)) overlap += 1;
      }
      return {
        name: s.name,
        description: s.description ?? '',
        score: overlap,
      };
    });

    scored.sort((a, b) => b.score - a.score);
    const top = scored.filter((s) => s.score > 0).slice(0, limit);
    return {
      goal,
      skills: top.map((s) => ({
        name: s.name,
        description: s.description,
        relevanceScore: s.score,
      })),
      ...(top.length === 0
        ? { note: 'No installed Skills matched. Install some from the Marketplace first.' }
        : {}),
    };
  },
};
